import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, ArrowRight, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { clients } from '../mock/data';

// Worksuite Lead Capture API
const LEAD_API_URL = 'https://api.worksuite.cl/v1/crm-capture-lead';
const LEAD_API_KEY = 'sb_publishable_pikt-oExOFtm-ZaozkAwaQ_qKqqyb91';
const LEAD_ORG_ID = '69d8e0bb-ed36-43c1-9416-d794e7f75ee0';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  company: '',
  job_title: '',
  business_type: '',
  company_size: '',
  solution_interest: '',
  message: ''
};

const DemoPage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const benefits = [
    'Ver demostración personalizada en vivo',
    'Conocer todas las funcionalidades de la plataforma',
    'Recibir asesoría sobre qué módulos necesitas',
    'Obtener cotización personalizada',
    'Configuración y onboarding incluido'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(LEAD_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: LEAD_API_KEY
        },
        body: JSON.stringify({
          ...formData,
          org_id: LEAD_ORG_ID,
          source: 'webhook',
          source_detail: 'Landing principal'
        })
      });

      if (response.ok) {
        setSubmitStatus('success');

        // Track Google Ads conversion
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion_event_submit_lead_form');
        }

        setFormData(INITIAL_FORM);
      } else {
        throw new Error('Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const inputClass =
    'w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600 disabled:bg-gray-100';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-grow pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              {/* Left Column - Info */}
              <div className="lg:sticky lg:top-32">
                <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
                  Hablemos de tu Proyecto
                </h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Descubre cómo Cloudbase puede transformar la gestión de tu negocio. Agenda una demostración o conversemos sobre tus necesidades.
                </p>

                <div className="space-y-4 mb-12">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* Trust indicators */}
                <div className="bg-gray-50 p-8 rounded-lg">
                  <p className="text-sm text-gray-600 mb-4 font-semibold uppercase tracking-wider">
                    Empresas que confían en nosotros
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    {clients.slice(0, 6).map((client, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-center grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                      >
                        <img
                          src={client.logo}
                          alt={client.name}
                          className="h-12 w-auto max-w-full object-contain"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Form */}
              <Card className="p-8 lg:p-12 shadow-2xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Cuéntanos sobre tu empresa
                </h2>

                {/* Success Message */}
                {submitStatus === 'success' && (
                  <div
                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
                    data-testid="demo-form-success"
                  >
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900">
                          ¡Gracias! Te contactaremos pronto.
                        </p>
                        <p className="text-sm text-green-700 mt-1">
                          Nuestro equipo se pondrá en contacto contigo en las próximas 24 horas.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div
                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
                    data-testid="demo-form-error"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5">✕</div>
                      <div>
                        <p className="font-semibold text-red-900">Error al enviar el formulario</p>
                        <p className="text-sm text-red-700 mt-1">
                          Por favor, intenta nuevamente o escríbenos a soporte@cloudbase.cl.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form
                  id="lead-form"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  data-testid="demo-lead-form"
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={inputClass}
                      placeholder="Juan Pérez"
                      data-testid="demo-input-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={inputClass}
                      placeholder="juan@empresa.cl"
                      data-testid="demo-input-email"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className={inputClass}
                        placeholder="+56 9 1234 5678"
                        data-testid="demo-input-phone"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Empresa
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className={inputClass}
                        placeholder="Mi Empresa S.A."
                        data-testid="demo-input-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cargo / Rol
                    </label>
                    <input
                      type="text"
                      name="job_title"
                      value={formData.job_title}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={inputClass}
                      placeholder="Gerente de Operaciones"
                      data-testid="demo-input-job-title"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tipo de negocio
                      </label>
                      <select
                        name="business_type"
                        value={formData.business_type}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className={inputClass}
                        data-testid="demo-input-business-type"
                      >
                        <option value="">Selecciona...</option>
                        <option value="SaaS">SaaS</option>
                        <option value="Retail">Retail</option>
                        <option value="Servicios">Servicios</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tamaño
                      </label>
                      <select
                        name="company_size"
                        value={formData.company_size}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className={inputClass}
                        data-testid="demo-input-company-size"
                      >
                        <option value="">Selecciona...</option>
                        <option value="1-10">1-10</option>
                        <option value="11-50">11-50</option>
                        <option value="51-200">51-200</option>
                        <option value="200+">200+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ¿Qué solución te interesa?
                    </label>
                    <select
                      name="solution_interest"
                      value={formData.solution_interest}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={inputClass}
                      data-testid="demo-input-solution"
                    >
                      <option value="">Selecciona...</option>
                      <option value="CRM">CRM</option>
                      <option value="ERP">ERP</option>
                      <option value="RRHH">RRHH</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ¿Cómo podemos ayudarte?
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={inputClass}
                      placeholder="Cuéntanos brevemente qué necesitas..."
                      data-testid="demo-input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="demo-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar Solicitud
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    Al enviar este formulario, aceptas nuestra{' '}
                    <a href="/privacidad" className="text-green-600 hover:underline">
                      política de privacidad
                    </a>
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DemoPage;
